import * as React from 'react'
import { array, boolean, option, ord, record, string } from 'fp-ts'
import { pipe } from 'fp-ts/lib/function'
import AmountInput from '../AmountInput'
import UnitSelect from '../UnitSelect'
import { Button, List } from 'antd'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  memoed,
  SimpleUnits,
  useOrganization,
  useUnits,
} from '@library/react-toolkit'
import styled from 'styled-components'

type Props = {
  onChange?: (_: Record<string, number> | undefined) => void
  defaultValue?: Record<string, number>
  value?: Record<string, number>
}

const PriceInputRow = styled(List.Item)`
  display: flex;
  padding: 4px 0;
  & .ant-input-number {
    flex: 1;
    margin-left: 12px;
  }
`

const ordByName = (units: SimpleUnits): ord.Ord<[string, unknown]> =>
  pipe(
    option.getOrd(string.Ord),
    ord.contramap(v =>
      pipe(
        record.lookup(v[0], units),
        option.map(u => u.name)
      )
    )
  )

const validateAmount = (
  a: Record<string, number | undefined>
): a is Record<string, number> =>
  Object.keys(a).length > 0 &&
  pipe(
    a,
    record.foldMap(boolean.MonoidAll)(n => n !== undefined && n > 0)
  )

const ItemPrices = ({ defaultValue, value, onChange }: Props) => {
  const units = useUnits()
  const organization = useOrganization()

  const currentValue = value ?? defaultValue
  const finalValue =
    !currentValue || record.size(currentValue) === 0
      ? { [organization?.asset ?? '']: 0 }
      : currentValue

  const intl = useIntl()
  const [val, setVal] =
    React.useState<Record<string, number | undefined>>(finalValue)

  React.useEffect(() => {
    if (!onChange) {
      return
    }
    onChange(validateAmount(val) ? val : undefined)
  }, [val])

  return (
    <List
      split={false}
      locale={{ emptyText: <FormattedMessage id="noPrice" /> }}
      rowKey={v => v[0]}
      footer={
        <UnitSelect
          value={intl.formatMessage({ id: 'selectAsset' })}
          placeholder={<FormattedMessage id="selectAsset" />}
          filter={unitID => option.isNone(record.lookup(unitID, val))}
          onChange={value => {
            setVal({ ...val, [value]: undefined })
          }}
        />
      }
      dataSource={pipe(record.toArray(val), array.sort(ordByName(units)))}
      renderItem={([unitID, amount]) => (
        <PriceInputRow>
          <Button
            onClick={() => {
              const { [unitID]: _, ...rest } = val
              setVal(rest)
            }}
          >
            <FormattedMessage id="removePrice" />
          </Button>
          <AmountInput
            value={amount}
            unit={unitID}
            onChange={n => setVal({ ...val, [unitID]: n })}
          />
        </PriceInputRow>
      )}
    />
  )
}

export default ItemPrices
